import get from 'lodash/get';
import mapKeys from 'lodash/mapKeys';
import { activeSponsors } from '../../values/sponsor';
import { StoreModifier } from '../../utilities/storeModifiers';

/**
 * Tracking Data State Object
 *
 * @type object
 */
const state = {
  autopilotSessionKey: null,
  googleVisitorId: null,
  lenderId: null,
  liveChatId: null,
  sponsorId: null,
  sponsorClickId: null,
  campaignId: null,
  templateId: null,
  workflow: null,
  utm: {
    source: null,
    campaign: null,
    medium: null,
    term: null,
    content: null
  },
  marketingAutomation: {
    Email: null,
    FirstName: null,
    LastName: null,
    unsubscribed: true,
    custom: {}
  },
  attributionLog: {
    attributions: []
  }
};

const getters = {

  /**
   * Returns Autopay data from the attribution log
   *
   * @param state
   * @param getters
   * @param rootState
   * @param rootGetters
   * @returns {Object}
   */
  autopayData: (state, getters, rootState, rootGetters) => {
    const data = get(state, 'attributionLog.attributions.0.autopayData.a', {});

    if (rootGetters['offer/hasMailCode']) {
      data.partnerRecordUniqueIdentifier = rootGetters['offer/mailCode'];
    }

    return data;
  },

  /**
   * Log of attribution states.
   *
   * @param state
   * @returns {getters.attributionLog|(function(*))|state.attributionLog|{attributions}|*}
   */
  attributionLog: state => state.attributionLog,

  /**
   * Template id passed in as tid
   *
   * @param state
   * @returns {getters.templateId|(function(*))|null}
   */
  templateId: state => state.templateId,
  /**
   * Gets the referring Sponsor ID
   *
   * @param state {object}
   * @returns {(number|null)}
   */
  sponsorId: state => state.sponsorId,

  /**
   *
   * @param state {object}
   * @returns {(number|null)}
   */
  sponsorClickId: state => state.sponsorClickId,

  /**
   * If the application is Direct Mail, gets the mailing lender ID
   *
   * @param state {object}
   * @returns {(number|null)}
   */
  lenderId: state => state.lenderId,

  /**
   * campaign ID aka cid query param
   *
   * @param state
   * @returns {getters.campaignId|(function(*))|null|*}
   */
  campaignId: state => state.campaignId,

  /**
   * Is the applicant a Direct Mail respondent?
   *
   * @param state {object}
   * @returns {boolean}
   */
  isDirectMail: state => state.lenderId !== null,

  /**
   * Gets the GA/GTM Visitor ID
   *
   * @param state {object}
   * @returns {(number|null)}
   */
  googleVisitorId: state => state.googleVisitorId,

  /**
   * Gets the active Live Chat ID
   *
   * @param state {object}
   * @returns {(number|null)}
   */
  liveChatId: state => state.liveChatId,

  /**
   * Gets the active Autopilot ID
   *
   * @param state {object}
   * @returns {(string|number|null)}
   */
  autoPilotSessionKey: state => state.autoPilotSessionKey,

  autoPilotPayload: state => state.marketingAutomation,

  /**
   * Gets the entire parsed UTM object
   *
   * @param state {object}
   * @returns {(Object|{utm_campaign, utm_term, utm_source, utm_medium, utm_content})}
   */
  utms: state => {
    return mapKeys(state.utm, (value, key) => {
      return `utm_${key}`;
    });
  },

  /**
   * Gets a UTM value by its name
   *
   * Usage:
   * `const source = utm('source');`
   *
   * @param state {object}
   * @returns {function(*): {string}}
   */
  utm: state => key => state.utm[key],
  workflow: state => state.workflow,
  /**
   * For assembling UTM data in payload
   *
   * @param state
   * @param getters
   * @returns {string}
   */
  utmString: (state, getters) => {
    return JSON.stringify({
      WORKFLOW: getters.workflow.toUpperCase(),
      WHICH_FORM: 'FASTTRACK',
      cid: getters.campaignId,
      subid: getters.sponsorClickId,
      tid: getters.templateId,
      visitorid: getters.googleVisitorId,
      ...getters.utms,
      ...getters.attributionLog
    });
  }
};

const actions = {
  /**
   * Sets the Sponsor ID
   * @param commit {function(*)}
   * @param sponsorId {number}
   */
  setSponsorId ({ commit }, sponsorId) {
    if (sponsorId === null) {
      sponsorId = activeSponsors.RATEGENIUS_NEW_APPLICATION;
    }

    commit('SET_SPONSOR_ID', sponsorId);
  },

  /**
   * Sets the template ID
   *
   * @param commit
   * @param templateId
   */
  setTemplateId ({ commit }, templateId) {
    commit('SET_TEMPLATE_ID', templateId);
  },

  /**
   * Sets the `subid` or Sponsor Click ID
   *
   * @param commit {function(*)}
   * @param sponsorClickId {(string|null)}
   */
  setSponsorClickId ({ commit }, sponsorClickId) {
    commit('SET_SPONSOR_CLICK_ID', sponsorClickId);
  },
  /**
   *
   * @param commit {function(*)}
   * @param lenderId {(number|null)}
   */
  setLenderId ({ commit }, lenderId) {
    commit('SET_LENDER_ID', lenderId);
  },
  /**
   *
   * @param commit {function(*)}
   * @param googleVisitorId {(string|number|null)}
   */
  setGoogleVisitorId ({ commit }, googleVisitorId) {
    commit('SET_GOOGLE_VISITOR_ID', googleVisitorId);
  },
  /**
   *
   * @param commit {function(*)}
   * @param liveChatId {(string|number|null)}
   */
  setLiveChatId ({ commit }, liveChatId) {
    commit('SET_LIVE_CHAT_ID', liveChatId);
  },
  /**
   * Sets the Autopilot Session Key
   *
   * @param commit {function(*)}
   * @param autopilotSessionKey {(string|number|null)}
   */
  setAutopilotSessionKey ({ commit }, autopilotSessionKey) {
    commit('SET_AUTOPILOT_SESSION_KEY', autopilotSessionKey);
  },
  /**
   * Sets the UTMs as a pre-built object
   *
   * @param commit {function(*)}
   * @param utms {object|{campaign: string, source: string, medium: string, content: string, term: string}}
   */
  setUtmAsObject ({ commit }, utms) {
    commit('SET_ALL_UTMS', utms);
  },
  /**
   * Sets an indivudal UTM value.
   *
   * Usage:
   *   Requires an object with a `name` and `value` property.
   *
   * @param commit {function(*)}
   * @param utm {Object|{name: string, value: string}}
   */
  setUtmValue ({ commit }, utm) {
    commit('SET_UTM_VALUE', utm);
  },

  setMarketingAutomationFields ({ commit }, automationFields) {
    commit('ADD_MARKETING_AUTOMATION_FIELD', automationFields);
  },

  setMarketingAutomationUnsubscribed ({ commit }, unsubscribed) {
    commit('ADD_MARKETING_AUTOMATION_FIELD', { unsubscribed });
  },

  /**
   * Set the attribution log
   *
   * @param commit
   * @param attributionLog
   */
  setAttributionLog ({ commit }, attributionLog) {
    commit('SET_ATTRIBUTION_LOG', attributionLog);
  },

  /**
   * Set campaign id
   *
   * @param commit
   * @param campaignId
   */
  setCampaignId ({ commit }, campaignId) {
    commit('SET_CAMPAIGN_ID', campaignId);
  },
  /**
   * Set the active workflow for this session
   *
   * @param commit
   * @param {string} activeWorkflow
   */
  setWorkflow ({ commit }, activeWorkflow) {
    commit('SET_WORKFLOW', activeWorkflow);
  }
};

const mutations = {
  SET_TEMPLATE_ID: (state, templateId) => {
    state.templateId = templateId;
  },
  SET_SPONSOR_ID: (state, sponsorId) => {
    state.sponsorId = sponsorId;
  },
  SET_SPONSOR_CLICK_ID: (state, sponsorClickId) => {
    state.sponsorClickId = sponsorClickId;
  },
  SET_LENDER_ID: (state, lenderId) => {
    state.lenderId = lenderId;
  },
  SET_GOOGLE_VISITOR_ID: (state, googleVisitorId) => {
    state.googleVisitorId = googleVisitorId;
  },
  SET_LIVE_CHAT_ID: (state, liveChatId) => {
    state.liveChatId = liveChatId;
  },
  SET_AUTOPILOT_SESSION_KEY: (state, autopilotSessionKey) => {
    state.autopilotSessionKey = autopilotSessionKey;
  },
  SET_ALL_UTMS: (state, utms) => {
    state.utm = utms;
  },
  SET_UTM_VALUE: (state, { name, value }) => {
    state.utm[name] = value;
  },
  ADD_MARKETING_AUTOMATION_FIELD: (state, automationFields) => {
    state.marketingAutomation = { ...state.marketingAutomation, ...automationFields };
  },
  SET_ATTRIBUTION_LOG: (state, attributionLog) => {
    state.attributionLog = { ...state.attributionLog, ...attributionLog };
  },
  SET_CAMPAIGN_ID: (state, campaignId) => {
    state.campaignId = campaignId;
  },
  SET_WORKFLOW: (state, activeWorkflow) => {
    state.workflow = activeWorkflow;
  }
};

export default new StoreModifier({
  actions,
  getters,
  mutations,
  namespaced: true,
  state
});
